import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type AnyRoute,
} from "@tanstack/react-router";

import { surfaces, type Surface } from "./surfaces/registry";
import { NoSurfaces, NotFound, Shell } from "./shell/shell";
import { uk } from "./i18n/uk";

// Адреса собираются из реестра поверхностей, а не перечисляются здесь: список
// адресов в общем файле был бы вторым реестром поверхностей и разошёлся бы с
// первым на первой же новой теке.

function Loading() {
  // ⚠ `role="status"` обязателен: поверхности грузятся при первом открытии, и
  // человек за экранным чтецом иначе не узнаёт, что страница вообще чем-то
  // занята — он слышит тишину и решает, что переход не сработал.
  return (
    <p role="status" className="text-ink-2">
      {uk.shell.loading}
    </p>
  );
}

function Failed() {
  return <p className="text-ink-2">{uk.shell.failed}</p>;
}

/**
 * Дерево адресов по данному перечню поверхностей.
 *
 * Отдельной функцией — чтобы его можно было собрать в тесте на подставленном
 * перечне, не поднимая сборку и не заводя настоящих поверхностей.
 */
export function buildRouteTree(found: Surface[]): AnyRoute {
  const root = createRootRoute({ component: Shell, notFoundComponent: NotFound });

  // Начало. Поверхностей нет — состояние каркаса словами. Есть дашборд `S-52` —
  // ведём на него: он стартовая поверхность всей системы (`G71`, `G93`); без
  // входа он сам отправит на вход (перехват окончания сессии в оболочке).
  // Дашборда в сборке нет — на первую поверхность. Пустого экрана нет ни в
  // одном из состояний.
  const first = found.find((one) => one.code === "S-52") ?? found[0];
  const index = createRoute({
    getParentRoute: () => root,
    path: "/",
    ...(first === undefined
      ? { component: NoSurfaces }
      : { beforeLoad: () => { throw redirect({ to: first.path }); } }),
  });

  const pages = found.map((one) =>
    createRoute({
      getParentRoute: () => root,
      path: one.path,
      // Экран грузится при открытии, а не при старте клиента: поверхностей
      // в системе десятки, и все они на входе не нужны.
      component: lazyRouteComponent(one.load),
      pendingComponent: Loading,
      errorComponent: Failed,
    }),
  );

  // ⚠ Каталог компонентов — не поверхность. Кода `S-NN` у него нет, в реестре
  // поверхностей его нет, в меню он не показывается и в состав системы не
  // входит: он нужен разработке и ревизии интерфейса. Единственное место, где
  // он назван, — эта строка; адрес начинается с подчёркивания ровно затем,
  // чтобы его нельзя было спутать с поверхностью.
  const catalog = createRoute({
    getParentRoute: () => root,
    path: "/_catalog",
    component: lazyRouteComponent(() => import("./catalog/index")),
    pendingComponent: Loading,
    errorComponent: Failed,
  });

  return root.addChildren([index, catalog, ...pages]);
}

export const router = createRouter({
  routeTree: buildRouteTree(surfaces),
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
